/**
 * @voltxampere/schema — Wire
 *
 * Per SPECS §1.4. A Wire is a connected polyline linking points on the
 * same net. Continuity (adjacent points connect) and orthogonality (for
 * style === "orthogonal") are invariants that cross segment boundaries
 * and are enforced by `validateCircuitInvariants`, not by Zod alone.
 *
 * Sprint 52 ADR-0085 — additive `gauge` and `slack` fields.
 *
 *   - `gauge` ∈ {`20AWG`, `22AWG`, `24AWG`, `28AWG`} controls the rendered
 *     stroke width per VISION §6.E "wire weight parametresi"; absent
 *     means "renderer falls back to its theme default".
 *   - `slack` ∈ [1.0, 1.3] is the catenary arc-length / chord-length
 *     ratio consumed by `packages/ui` `catenary-math.ts`. Absent means
 *     the renderer's `DEFAULT_SLACK` (1.05) applies.
 *
 * Both fields are optional so existing `.vxa` documents (Sprint 12 → 51)
 * deserialise without migration. Schema version stays at 0.4.0 since
 * the underlying contract is purely additive.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
import { PointSchema } from "./_primitives.js";
export const WireStyleSchema = z.enum(["orthogonal", "direct"]);
export const WireGaugeSchema = z.enum(["20AWG", "22AWG", "24AWG", "28AWG"]);
/** Sprint 52 default slack ratio when a wire omits the field. */
export const DEFAULT_WIRE_SLACK = 1.05;
/**
 * Sprint 52 default gauge — 24AWG matches the `theme.stroke.default`
 * 2 px width that wires shipped with through Sprint 51.
 */
export const DEFAULT_WIRE_GAUGE = "24AWG";
export const WireSchema = z
    .object({
    id: z.string(),
    netId: z.string(),
    path: z.array(PointSchema).min(2),
    style: WireStyleSchema.default("orthogonal"),
    gauge: WireGaugeSchema.optional(),
    slack: z.number().min(1).max(1.3).optional(),
})
    .strict();
//# sourceMappingURL=wire.js.map